import { supabase } from '../lib/supabase';
import { UserProfile, UserStats, GameSession, UnlockedAchievement } from '../types/profile';
import { ACHIEVEMENTS } from '../data/achievements';
import { ProfileService } from './profileService'; // Fallback for guests

export class SupabaseProfileService {
  
  // Get or create profile (works for both auth and guest users)
  static async getOrCreateProfile(userId?: string): Promise<UserProfile> {
    console.log('getOrCreateProfile called with userId:', userId);
    
    // If no userId provided, fall back to guest profile
    if (!userId) {
      console.log('No userId provided, falling back to guest profile');
      return ProfileService.getOrCreateProfile();
    }

    try {
      console.log('Fetching profile from Supabase for userId:', userId);
      
      // Try to get existing profile from Supabase
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      console.log('Supabase query result:', { profile, error });

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        // Check if this is a network error (offline)
        if (error.message?.includes('fetch') || error.message?.includes('network') || !navigator.onLine) {
          console.warn('Network error detected, falling back to guest profile:', error.message);
          return ProfileService.getOrCreateProfile();
        }
        
        console.error('Database error fetching profile:', error);
        throw error;
      }

      if (profile) {
        console.log('Profile found, converting...');
        // Convert Supabase profile to our UserProfile format
        return await this.convertSupabaseProfile(profile);
      }

      // Profile doesn't exist, this shouldn't happen due to trigger
      // but let's handle it gracefully
      console.warn('Profile not found for authenticated user, this should not happen');
      return this.createAuthenticatedProfile(userId);

    } catch (error: any) {
      console.error('Error in getOrCreateProfile:', error);
      
      // Check if this is a network-related error
      if (error.message?.includes('fetch') || 
          error.message?.includes('network') || 
          error.message?.includes('Failed to fetch') ||
          !navigator.onLine) {
        console.warn('Network connectivity issue detected, using offline mode');
        return ProfileService.getOrCreateProfile();
      }
      
      // For other errors, still fall back to guest profile but log the error
      console.error('Unexpected error, falling back to guest profile:', error);
      return ProfileService.getOrCreateProfile();
    }
  }

  // Convert Supabase profile format to our UserProfile format
  private static async convertSupabaseProfile(supabaseProfile: any): Promise<UserProfile> {
    console.log('Converting Supabase profile:', supabaseProfile);
    
    // Load achievements for this user
    const achievements = await this.loadUserAchievements(supabaseProfile.id);
    
    const converted = {
      id: supabaseProfile.id,
      username: supabaseProfile.username,
      avatar: supabaseProfile.avatar,
      createdAt: supabaseProfile.created_at,
      lastActiveAt: supabaseProfile.last_active_at,
      stats: this.convertSupabaseStats(supabaseProfile.stats),
      achievements: achievements, // Now properly loaded from database
      preferences: supabaseProfile.preferences || {
        favoriteGameMode: 'classic',
        preferredDifficulty: 'medium',
        soundEnabled: true,
        animationsEnabled: true
      }
    };

    console.log('Converted profile with achievements:', { ...converted, achievementCount: achievements.length });
    return converted;
  }

  // Convert Supabase stats format to our UserStats format
  private static convertSupabaseStats(supabaseStats: any): UserStats {
    if (!supabaseStats) {
      return this.createEmptyStats();
    }

    // Map Supabase stats structure to our structure
    return {
      totalGames: supabaseStats.totalGames || 0,
      totalScore: supabaseStats.totalScore || 0,
      bestScore: supabaseStats.bestScore || 0,
      longestStreak: supabaseStats.bestStreak || 0,
      currentStreak: 0, // This is session-based, not stored
      gamesWon: 0, // Calculate from accuracy
      totalTimePlayed: supabaseStats.totalTimePlayed || 0,
      averageAccuracy: supabaseStats.averageAccuracy || 0,
      measurementStats: {
        length: {
          gamesPlayed: supabaseStats.gamesByMode?.measurement_focus || 0,
          questionsAnswered: 0,
          correctAnswers: 0,
          totalScore: 0,
          averageAccuracy: supabaseStats.accuracyByMeasurement?.length || 0,
          bestStreak: 0
        },
        weight: {
          gamesPlayed: 0,
          questionsAnswered: 0,
          correctAnswers: 0,
          totalScore: 0,
          averageAccuracy: supabaseStats.accuracyByMeasurement?.weight || 0,
          bestStreak: 0
        },
        volume: {
          gamesPlayed: 0,
          questionsAnswered: 0,
          correctAnswers: 0,
          totalScore: 0,
          averageAccuracy: supabaseStats.accuracyByMeasurement?.volume || 0,
          bestStreak: 0
        }
      },
      gameModeStats: this.convertGameModeStats(supabaseStats.gamesByMode || {})
    };
  }

  // Convert game mode stats
  private static convertGameModeStats(gamesByMode: any): any {
    const gameModeStats: any = {};
    
    Object.keys(gamesByMode).forEach(mode => {
      gameModeStats[mode] = {
        gamesPlayed: gamesByMode[mode] || 0,
        bestScore: 0,
        averageScore: 0,
        totalTimePlayed: 0
      };
    });

    return gameModeStats;
  }

  // Create empty stats structure
  private static createEmptyStats(): UserStats {
    return {
      totalGames: 0,
      totalScore: 0,
      bestScore: 0,
      longestStreak: 0,
      currentStreak: 0,
      gamesWon: 0,
      totalTimePlayed: 0,
      averageAccuracy: 0,
      measurementStats: {
        length: { gamesPlayed: 0, questionsAnswered: 0, correctAnswers: 0, totalScore: 0, averageAccuracy: 0, bestStreak: 0 },
        weight: { gamesPlayed: 0, questionsAnswered: 0, correctAnswers: 0, totalScore: 0, averageAccuracy: 0, bestStreak: 0 },
        volume: { gamesPlayed: 0, questionsAnswered: 0, correctAnswers: 0, totalScore: 0, averageAccuracy: 0, bestStreak: 0 }
      },
      gameModeStats: {}
    };
  }

  // Create authenticated profile (fallback)
  private static async createAuthenticatedProfile(userId: string): Promise<UserProfile> {
    const now = new Date().toISOString();
    
    const profile: UserProfile = {
      id: userId,
      username: 'Player', // Will be updated by trigger
      avatar: '🍌',
      createdAt: now,
      lastActiveAt: now,
      stats: this.createEmptyStats(),
      achievements: [],
      preferences: {
        favoriteGameMode: 'classic',
        preferredDifficulty: 'medium',
        soundEnabled: true,
        animationsEnabled: true
      }
    };

    return profile;
  }

  // Update profile after game completion
  static async updateProfileAfterGame(gameSession: GameSession, userId?: string): Promise<UnlockedAchievement[]> {
    // If no userId, fall back to local storage
    if (!userId) {
      return ProfileService.updateProfileAfterGame(gameSession);
    }

    // Check if we're offline
    if (!navigator.onLine) {
      console.warn('Offline detected, falling back to local storage for game session');
      return ProfileService.updateProfileAfterGame(gameSession);
    }

    try {
      // Get current profile
      const profile = await this.getOrCreateProfile(userId);
      
      // Save game session to Supabase
      await this.saveGameSession(gameSession, userId);
      
      // Update profile stats
      const updatedStats = await this.calculateUpdatedStats(profile.stats, gameSession, userId);
      
      // Update profile in Supabase
      await this.updateSupabaseProfile(userId, {
        stats: this.convertToSupabaseStats(updatedStats),
        last_active_at: new Date().toISOString()
      });

      // Check and save achievements
      const newAchievements = await this.checkAndSaveAchievements(profile, gameSession, userId);

      return newAchievements;

    } catch (error: any) {
      console.error('Error updating profile after game:', error);
      
      // Check if this is a network-related error
      if (error.message?.includes('fetch') || 
          error.message?.includes('network') || 
          error.message?.includes('Failed to fetch') ||
          !navigator.onLine) {
        console.warn('Network error during profile update, falling back to local storage');
      } else {
        console.error('Database error during profile update:', error);
      }
      
      // Fall back to local storage on any error
      return ProfileService.updateProfileAfterGame(gameSession);
    }
  }

  // Save game session to Supabase
  private static async saveGameSession(gameSession: GameSession, userId: string): Promise<void> {
    console.log('Saving game session:', { gameSession, userId });
    
    // First, let's check what columns exist by doing a simple select
    const { data: existingData, error: selectError } = await supabase
      .from('game_sessions')
      .select('*')
      .limit(1);
    
    console.log('Existing game_sessions structure:', existingData, selectError);
    
    const sessionData = {
      user_id: userId,
      game_mode: gameSession.gameMode,
      difficulty: gameSession.difficulty,
      measurement_type: gameSession.measurementType || null,
      score: gameSession.score,
      questions_answered: gameSession.questionsAnswered,
      correct_answers: gameSession.correctAnswers,
      streak: gameSession.streak,
      time_played: gameSession.timePlayed,
      started_at: gameSession.startedAt,
      completed_at: gameSession.completedAt
    };

    console.log('Session data to insert:', sessionData);

    const { data, error } = await supabase
      .from('game_sessions')
      .insert(sessionData)
      .select();

    if (error) {
      console.error('Error saving game session:', error);
      console.error('Full error details:', JSON.stringify(error, null, 2));
      throw error;
    }

    console.log('Game session saved successfully:', data);
  }

  // Calculate updated stats based on game session
  private static async calculateUpdatedStats(currentStats: UserStats, gameSession: GameSession, userId: string): Promise<UserStats> {
    // Get all game sessions for accurate calculations
    const { data: sessions, error } = await supabase
      .from('game_sessions')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching game sessions:', error);
      // Fall back to incremental update
      return this.incrementalStatsUpdate(currentStats, gameSession);
    }

    // Recalculate stats from all sessions
    return this.recalculateStatsFromSessions(sessions || []);
  }

  // Incremental stats update (fallback)
  private static incrementalStatsUpdate(stats: UserStats, gameSession: GameSession): UserStats {
    const newStats = { ...stats };
    
    newStats.totalGames += 1;
    newStats.totalScore += gameSession.score;
    newStats.bestScore = Math.max(newStats.bestScore, gameSession.score);
    newStats.totalTimePlayed += gameSession.timePlayed;
    newStats.longestStreak = Math.max(newStats.longestStreak, gameSession.streak);

    // Update accuracy
    const accuracy = gameSession.questionsAnswered > 0 ? 
      (gameSession.correctAnswers / gameSession.questionsAnswered) * 100 : 0;
    
    if (accuracy >= 70) {
      newStats.gamesWon += 1;
    }

    return newStats;
  }

  // Recalculate stats from all sessions
  private static recalculateStatsFromSessions(sessions: any[]): UserStats {
    const stats = this.createEmptyStats();
    
    stats.totalGames = sessions.length;
    stats.totalScore = sessions.reduce((sum, s) => sum + s.score, 0);
    stats.bestScore = Math.max(...sessions.map(s => s.score), 0);
    stats.totalTimePlayed = sessions.reduce((sum, s) => sum + s.time_played, 0);
    stats.longestStreak = Math.max(...sessions.map(s => s.streak), 0);

    // Calculate accuracy and games won
    let totalQuestions = 0;
    let totalCorrect = 0;
    
    sessions.forEach(session => {
      totalQuestions += session.questions_answered;
      totalCorrect += session.correct_answers;
      
      const accuracy = session.questions_answered > 0 ? 
        (session.correct_answers / session.questions_answered) * 100 : 0;
      if (accuracy >= 70) {
        stats.gamesWon += 1;
      }
    });

    stats.averageAccuracy = totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0;

    return stats;
  }

  // Convert our stats format to Supabase format
  private static convertToSupabaseStats(stats: UserStats): any {
    return {
      totalGames: stats.totalGames,
      bestScore: stats.bestScore,
      totalScore: stats.totalScore,
      bestStreak: stats.longestStreak,
      averageAccuracy: stats.averageAccuracy,
      totalTimePlayed: stats.totalTimePlayed,
      accuracyByMeasurement: {
        length: stats.measurementStats.length.averageAccuracy,
        weight: stats.measurementStats.weight.averageAccuracy,
        volume: stats.measurementStats.volume.averageAccuracy
      },
      gamesByMode: Object.keys(stats.gameModeStats).reduce((acc, mode) => {
        acc[mode] = stats.gameModeStats[mode].gamesPlayed;
        return acc;
      }, {} as any)
    };
  }

  // Update Supabase profile
  private static async updateSupabaseProfile(userId: string, updates: any): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId);

    if (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  // Check and save achievements
  private static async checkAndSaveAchievements(profile: UserProfile, gameSession: GameSession, userId: string): Promise<UnlockedAchievement[]> {
    // Get existing achievements
    const { data: existingAchievements } = await supabase
      .from('user_achievements')
      .select('achievement_id')
      .eq('user_id', userId);

    const unlockedIds = new Set(existingAchievements?.map(a => a.achievement_id) || []);
    const newAchievements: UnlockedAchievement[] = [];

    // Check each achievement using comprehensive logic
    for (const achievement of ACHIEVEMENTS) {
      if (unlockedIds.has(achievement.id)) continue;

      // Check if achievement should be unlocked
      if (this.checkAchievementCondition(achievement, profile.stats, gameSession)) {
        try {
          // Save to Supabase
          await supabase
            .from('user_achievements')
            .insert({
              user_id: userId,
              achievement_id: achievement.id,
              unlocked_at: new Date().toISOString(),
              progress: 100
            });

          newAchievements.push({
            id: achievement.id,
            unlockedAt: new Date().toISOString(),
            progress: 100
          });
        } catch (error) {
          console.error('Error saving achievement to database:', error);
        }
      }
    }

    return newAchievements;
  }
  
  // Check if achievement condition is met (comprehensive logic)
  private static checkAchievementCondition(achievement: any, stats: UserStats, session: GameSession): boolean {
    const { requirement } = achievement;
    
    switch (requirement.type) {
      case 'games_played':
        return stats.totalGames >= requirement.value;
      
      case 'total_score':
        // For high scorer achievement, check single game score
        if (achievement.id === 'high_scorer') {
          return session.score >= requirement.value;
        }
        // For cumulative score achievements
        return stats.totalScore >= requirement.value;
      
      case 'streak':
        // Check both current session streak and overall longest streak
        return Math.max(stats.longestStreak, session.streak) >= requirement.value;
      
      case 'measurement_games':
        if (requirement.measurementType && 
            (requirement.measurementType === 'length' || 
             requirement.measurementType === 'weight' || 
             requirement.measurementType === 'volume')) {
          const measurementType = requirement.measurementType as 'length' | 'weight' | 'volume';
          return stats.measurementStats[measurementType].gamesPlayed >= requirement.value;
        }
        // For measurement master - check if all measurement types have enough games
        if (achievement.id === 'measurement_master') {
          return Object.values(stats.measurementStats).every(stat => stat.gamesPlayed >= 15);
        }
        return false;
      
      case 'game_mode_games':
        if (requirement.difficulty) {
          // This would need difficulty tracking per game - simplified for now
          return Object.values(stats.gameModeStats).reduce((total, modeStat) => total + modeStat.gamesPlayed, 0) >= requirement.value;
        }
        // For mode explorer - check if played different game modes
        if (achievement.id === 'mode_explorer') {
          return Object.keys(stats.gameModeStats).length >= requirement.value;
        }
        return Object.keys(stats.gameModeStats).length >= requirement.value;
      
      case 'perfect_questions':
        // This would need to be tracked separately in stats - simplified for now
        // Estimate based on very high accuracy
        const estimatedPerfect = Math.floor((stats.averageAccuracy - 90) / 5);
        return estimatedPerfect >= requirement.value;
      
      default:
        return false;
    }
  }

  // Update user preferences
  static async updatePreferences(updates: any, userId?: string): Promise<void> {
    if (!userId) {
      return ProfileService.updatePreferences(updates);
    }

    const { error } = await supabase
      .from('profiles')
      .update({ preferences: updates })
      .eq('id', userId);

    if (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  }

  // Update profile info (username, avatar)
  static async updateProfile(updates: { username?: string; avatar?: string }, userId?: string): Promise<void> {
    if (!userId) {
      return ProfileService.updateProfile(updates);
    }

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId);

    if (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  // Load achievements for authenticated user
  static async loadUserAchievements(userId: string): Promise<UnlockedAchievement[]> {
    try {
      const { data: achievements, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', userId);

      if (error) {
        console.error('Error loading achievements:', error);
        return [];
      }

      return achievements?.map(a => ({
        id: a.achievement_id,
        unlockedAt: a.unlocked_at,
        progress: a.progress || 100
      })) || [];
    } catch (error) {
      console.error('Error in loadUserAchievements:', error);
      return [];
    }
  }
} 